import type { WorkflowNode } from "@/types/aiToolkit";
import type { NodeOutput, WorkflowRunState } from "@/types/workflowEngine";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { applyDataMapping } from "./graphTraverser";

export async function executeKnowledgeNode(
  node: WorkflowNode,
  state: WorkflowRunState
): Promise<NodeOutput> {
  const source = node.data.knowledgeSource ?? "trigger";
  const config = node.data.knowledgeConfig ?? {};
  let content = "";
  let title = node.data.label || "Context";

  try {
    if (source === "trigger") {
      const body = state.variables.body ?? state.variables.content ?? state.variables.transcript;
      content = typeof body === "string" ? body : JSON.stringify(body ?? state.variables, null, 2);
      title = String(state.variables.subject ?? state.variables.title ?? "Trigger payload");
    } else if (source === "kb" && config.documentId) {
      const doc = await knowledgeBaseService.getKnowledgeBaseItem(String(config.documentId));
      content = doc?.content ?? "";
      title = doc?.title ?? "KB Document";
    } else if (source === "thread" && config.threadId) {
      const thread = await chatThreadsService.getChatThread(String(config.threadId));
      const messages = Array.isArray(thread?.messages) ? thread.messages : [];
      content = messages.map((m: { role?: string; content?: unknown }) => `${m.role}: ${m.content}`).join("\n");
      title = thread?.title ?? "Thread";
    } else if (source === "integration") {
      content = String(state.variables.body ?? state.variables.content ?? "");
      title = String(state.variables.subject ?? "Integration content");
    } else if (source === "space") {
      content = JSON.stringify(state.variables, null, 2);
      title = "Space context";
    }
  } catch (e) {
    return { raw: "", parsed: { error: e instanceof Error ? e.message : "Knowledge load failed" } };
  }

  state.contextBlocks[node.id] = { source, title, content, metadata: config as Record<string, unknown> };
  return { raw: content, parsed: { title, source } };
}

export function executeTransformNode(node: WorkflowNode, state: WorkflowRunState): NodeOutput {
  const mapped = applyDataMapping(node.data.toolInputMapping, {
    variables: state.variables,
    nodeOutputs: state.nodeOutputs,
    contextBlocks: state.contextBlocks,
  } as unknown as Record<string, unknown>);
  return { raw: JSON.stringify(mapped, null, 2), parsed: mapped };
}

export function executeValidationNode(node: WorkflowNode, state: WorkflowRunState, priorNodeId?: string): NodeOutput {
  const prior = priorNodeId ? state.nodeOutputs[priorNodeId] : Object.values(state.nodeOutputs).pop();
  const schema = node.data.validationSchema;
  let valid = true;
  const errors: string[] = [];

  if (prior?.parsed && typeof prior.parsed === "object") {
    const p = prior.parsed as Record<string, unknown>;
    if (schema && typeof schema === "object" && "required" in schema) {
      const required = (schema as { required?: string[] }).required ?? [];
      for (const field of required) {
        if (p[field] === undefined || p[field] === null) {
          valid = false;
          errors.push(`Missing required field: ${field}`);
        }
      }
    }
  } else if (node.data.validationSchema) {
    valid = false;
    errors.push("Prior output is not valid JSON object");
  }

  return {
    raw: valid ? "Validation passed" : `Validation failed: ${errors.join(", ")}`,
    parsed: { valid, errors },
    validation: { valid, errors },
  };
}

export function executeConfidenceGate(node: WorkflowNode, state: WorkflowRunState, priorNodeId?: string): NodeOutput {
  const threshold = node.data.confidenceThreshold ?? 0.7;
  const prior = priorNodeId ? state.nodeOutputs[priorNodeId] : Object.values(state.nodeOutputs).pop();
  const confidence = prior?.confidence ?? (prior?.parsed as Record<string, number>)?.confidence ?? 0;
  const pass = confidence >= threshold;
  return {
    raw: pass ? "confidence_pass" : "confidence_fail",
    parsed: { confidence, threshold, pass },
    confidence,
  };
}

export function executeQualityGate(node: WorkflowNode, state: WorkflowRunState, priorNodeId?: string): NodeOutput {
  const threshold = node.data.qualityThreshold ?? 0.6;
  const prior = priorNodeId ? state.nodeOutputs[priorNodeId] : Object.values(state.nodeOutputs).pop();
  const quality = prior?.quality ?? (prior?.parsed as Record<string, number>)?.quality ?? 0;
  const pass = quality >= threshold;
  return {
    raw: pass ? "quality_pass" : "quality_fail",
    parsed: { quality, threshold, pass },
  };
}

export function executeConditionNode(
  node: WorkflowNode,
  state: WorkflowRunState,
  priorNodeId?: string
): NodeOutput {
  const field = node.data.condition ?? "spam";
  const expected = node.data.conditionValue ?? true;
  const prior = priorNodeId ? state.nodeOutputs[priorNodeId] : Object.values(state.nodeOutputs).pop();
  const parsed = prior?.parsed as Record<string, unknown> | undefined;
  const actual = parsed?.[field];
  const pass =
    actual === expected ||
    (typeof expected === "boolean" && Boolean(actual) === expected);
  return {
    raw: pass ? "true" : "false",
    parsed: { field, expected, actual, pass },
  };
}

export function executeEntityResolutionNode(state: WorkflowRunState): NodeOutput {
  const people: Array<{ name: string; email?: string }> = [];
  const dates: Array<{ raw: string; iso?: string }> = [];
  for (const out of Object.values(state.nodeOutputs)) {
    const parsed = out.parsed as Record<string, unknown> | undefined;
    if (!parsed) continue;
    const deliverables = parsed.deliverables as Array<{ owner?: string }> | undefined;
    if (Array.isArray(deliverables)) {
      for (const d of deliverables) {
        if (d.owner) people.push({ name: d.owner });
      }
    }
  }
  state.entities = { people, dates };
  return { raw: JSON.stringify(state.entities, null, 2), parsed: state.entities };
}

export function executeMergeNode(node: WorkflowNode, state: WorkflowRunState): NodeOutput {
  const buffer = state.parallel.mergeBuffers[node.id] ?? [];
  const strategy = node.data.mergeStrategy ?? "all";
  const merged = buffer.map((b) => b.parsed ?? b.raw);
  return {
    raw: JSON.stringify(merged, null, 2),
    parsed: { strategy, branches: merged, count: buffer.length },
  };
}
